export default angular.module('eventix.common.services.languageLoader',[])
/**
 * @ngdoc service
 * @name eventix.common.services.languageLoader
 * @description
 * Provides AngularTranslate with the means of fetching translations and allows the
 * user to store customizations. The main constructor function is used by `pascalprecht.translate`
 * as a custom loader. The service has additional methods for customizing the language file.
 *
 * @param {Object} options Which language to load
 * ```
 * {
 *      key: 'en',
 *      group: 'dashboard'
 * }
 * ```
 * @returns {Object} Translations on a promise
 */
    .provider('languageLoader', function(LocalesProvider) {
        this.$get = /*@ngInject*/function($http, $q, Locales) {
            var locale, group, translations = {};

            /**
             * Load a locale and return the translations on a promise
             *
             * @param {Object} options Which language to load
             * @returns {Object} Translations on a promise
             */
            function languageLoader(options) {
                group = options.group;
                locale = options.key;

                if (!_.find(Locales, {code: options.key}).translation) {
                    locale = LocalesProvider.mapping[options.key.slice(0, 3) + '*'];
                }

                if (translations[locale]) {
                    return $q.resolve(translations[locale]);
                }

                let url = DASHBOARD_API_ROOT + `/lang/${locale}/${group}/`;

                if(typeof USE_SHOP_CACHED !== 'undefined' && USE_SHOP_CACHED == 1){
                    url = 'https://ev3b.s3-eu-west-1.amazonaws.com/peak/lang.' + locale + '.' + group +  '.json';
                }
                else {
                    if (options.differential) {
                        url += `${options.differential}/`;

                        if (options.diffType) {
                            url += `${options.diffType}/`;
                        }
                    }
                }

                // Gotta do the prefix because the language api endpoint
                // is not available on the shop subdomain
                return $http.get(url)
                    .then(res => {
                        translations[locale] = res.data;

                        return $q.resolve(translations[locale]);
                    })
                    .catch(() => $q.reject(options.key));
            }

            /**
             * @ngdoc function
             * @name eventix.common.services.languageLoader#loadAll
             * @methodOf eventix.common.services.languageLoader
             * @description
             * Load all translations
             *
             * @param {String} group Which language group to load
             * @returns {Object} Promise of translation object, keyed by language
             * ```
             * {
             *      en: {},
             *      nl: {}
             * }
             * ```
             */
            languageLoader.loadAll = function(group = 'dashboard') {
                var promises = _.map(Locales, locale => languageLoader({key: locale.code, group: group}));
                return $q.all(promises).then(results => $q.resolve(translations));
            };

            /**
             * @ngdoc function
             * @name eventix.common.services.languageLoader#save
             * @methodOf eventix.common.services.languageLoader
             * @description
             * Save custom translations
             *
             * @example
             * <pre>
             * languageLoader.save([{
             *      key: 'common.menu.settings',
             *      locales: [{
             *          locale: 'en',
             *          value: 'Settings'
             *      }]
             * }], 'dashboard');
             * </pre>
             *
             * @param {Object} customTranslations Formatted translations to save to the DB
             * @param {String} group Group of translations, defaults to 'dashboard'
             * @returns {Promise} $http response
             */
            languageLoader.save = function(customTranslations, group = 'dashboard') {
                return $http.post(`/lang/${group}`, customTranslations);
            };

            /**
             * @ngdoc function
             * @name eventix.common.services.languageLoader#resetLocale
             * @methodOf eventix.common.services.languageLoader
             * @description
             * Reset a locale, clearing the custom translations
             *
             * @param {String} locale Defaults to 'en'
             * @returns {Promise} $http response
             */
            languageLoader.resetLocale = function(locale = 'en') {
                return $http.delete(`/lang/${locale}`);
            };

            /**
             * @ngdoc function
             * @name eventix.common.services.languageLoader#resetGroup
             * @methodOf eventix.common.services.languageLoader
             * @description
             * Reset a group for a locale, clearing the custom translations
             *
             * @param {String} locale Defaults to 'en'
             * @param {String} group Defaults to 'dashboard'
             * @returns {Promise} $http response
             */
            languageLoader.resetGroup = function(locale = 'en', group = 'dashboard') {
                return $http.delete(`/lang/${locale}/${group}`);
            };

            return languageLoader;
        };
    }).name;
